import { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Button, Space, Spin, InputNumber, Tooltip, Modal, Input } from 'antd';
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2, FileText,
  AlertCircle, Printer, Download, ScanLine, Lock,
} from 'lucide-react';

// Worker setup (giống PdfSignaturePlacer)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * V1.0.40: PDF viewer dùng pdf.js — hoạt động trên iPad/iOS (thay thế <iframe>).
 *
 * Lý do: iOS Safari + Chrome iOS render <iframe src="*.pdf"> chỉ trang đầu, không có controls.
 * pdf.js render qua canvas → hoạt động đa nền tảng + có zoom/page controls đầy đủ.
 *
 * Props:
 *  - file: Blob | URL | string (path/Blob URL)
 *  - documentName: tên file (hiển thị header)
 *  - extraHeaderRight: button phụ ở header bên phải (vd: toggle Original/Signed)
 *  - height: chiều cao container (default 'calc(100vh - 250px)')
 */
interface Props {
  file: Blob | string | null;
  documentName?: string;
  extraHeaderRight?: React.ReactNode;
  height?: number | string;
}

export function PdfFullViewer({ file, documentName, extraHeaderRight, height }: Props) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [containerWidth, setContainerWidth] = useState<number>(800);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState<'fit-width' | 'continuous'>(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    return isMobile ? 'fit-width' : 'continuous';
  });
  // V1.0.63i: Password handling cho PDF có pass
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordWrong, setPasswordWrong] = useState(false);
  // Lưu callback từ react-pdf để truyền password sau khi user nhập
  const passwordCallbackRef = useRef<((pwd: string) => void) | null>(null);

  // Callback được react-pdf gọi khi PDF cần pass hoặc pass sai
  // reason: 1 = NEED_PASSWORD, 2 = INCORRECT_PASSWORD
  const handlePassword = useCallback((cb: (pwd: string) => void, reason: number) => {
    passwordCallbackRef.current = cb;
    setPasswordInput('');
    setPasswordWrong(reason === 2);  // reason=2 nghĩa pass trước đó sai
    setPasswordDialogOpen(true);
  }, []);

  const submitPassword = () => {
    if (!passwordInput.trim()) return;
    passwordCallbackRef.current?.(passwordInput);
    setPasswordDialogOpen(false);
    setLoading(true);  // chờ react-pdf reload với pass mới
  };

  const cancelPassword = () => {
    setPasswordDialogOpen(false);
    setLoading(false);
    setError('Tài liệu được bảo vệ bằng mật khẩu. Bạn cần nhập đúng mật khẩu để xem.');
    // Gửi pass rỗng để react-pdf trigger onLoadError
    passwordCallbackRef.current?.('');
  };

  // V1.0.41: Listen fullscreen change để cập nhật state khi user thoát ESC
  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Theo dõi resize container để fit width
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setContainerWidth(w);
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Reset page khi đổi file
  useEffect(() => {
    setPage(1);
    setNumPages(null);
    setError(null);
    setLoading(true);
  }, [file]);

  const onDocumentLoadSuccess = useCallback(({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setLoading(false);
  }, []);

  const onDocumentLoadError = useCallback((e: Error) => {
    console.error('PDF load error:', e);
    setError('Không tải được file PDF. File có thể bị hỏng hoặc không phải PDF hợp lệ.');
    setLoading(false);
  }, []);

  if (file === null) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#7d8a98' }}>
        <FileText size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
        <div>Chưa có file để hiển thị</div>
      </div>
    );
  }

  // Tính page width: scale * containerWidth (giảm 32 cho padding)
  const baseWidth = Math.max(320, containerWidth - 32);
  const pageWidth = baseWidth * scale;

  const handlePrev = () => setPage(p => Math.max(1, p - 1));
  const handleNext = () => setPage(p => Math.min(numPages ?? 1, p + 1));
  const handleZoomIn = () => setScale(s => Math.min(3, s + 0.25));
  const handleZoomOut = () => setScale(s => Math.max(0.5, s - 0.25));
  const handleFit = () => setScale(1.0);

  // V1.0.41: Print — mở iframe ẩn với PDF blob → invoke window.print
  const handlePrint = () => {
    if (!file) return;
    const url = typeof file === 'string' ? file : URL.createObjectURL(file);
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = url;
    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch (e) {
        console.error('Print failed:', e);
        window.open(url, '_blank');
      }
    };
    document.body.appendChild(iframe);
    // Cleanup sau 1 phút
    setTimeout(() => {
      try { document.body.removeChild(iframe); } catch { /* ignore */ }
      if (typeof file !== 'string') URL.revokeObjectURL(url);
    }, 60_000);
  };

  // V1.0.41: Download PDF
  const handleDownload = () => {
    if (!file) return;
    const url = typeof file === 'string' ? file : URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = documentName?.replace(/[/\\?%*:|"<>]/g, '_') ?? 'document.pdf';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    if (typeof file !== 'string') {
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };

  // V1.0.41: Toggle Fullscreen
  const handleFullscreen = () => {
    const el = wrapperRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch((e) => console.error('Fullscreen failed:', e));
    } else {
      document.exitFullscreen?.().catch(() => { /* ignore */ });
    }
  };

  return (
    <div
      ref={wrapperRef}
      style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        background: '#fff', borderRadius: 6, overflow: 'hidden',
        height: isFullscreen ? '100vh' : (height ?? 'calc(100vh - 250px)'),
        minHeight: 400,
      }}
    >
      {/* Header */}
      <div style={{
        padding: '8px 12px', borderBottom: '1px solid #e1e5eb',
        background: '#fafbfc', display: 'flex', alignItems: 'center',
        gap: 8, flexWrap: 'wrap',
      }}>
        <span style={{ flex: 1, minWidth: 100, fontSize: 12, color: '#5b6875', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          📄 <b>{documentName ?? 'Tài liệu'}</b>
        </span>

        {extraHeaderRight}

        {/* Page navigation — chỉ hiện khi mode fit-width */}
        {viewMode === 'fit-width' && numPages && (
          <Space.Compact size="small">
            <Button onClick={handlePrev} disabled={page <= 1} icon={<ChevronLeft size={12} />} />
            <InputNumber
              size="small"
              min={1}
              max={numPages}
              value={page}
              onChange={(v) => v && setPage(Number(v))}
              style={{ width: 60 }}
              controls={false}
            />
            <Button disabled style={{ pointerEvents: 'none' }}>/ {numPages}</Button>
            <Button onClick={handleNext} disabled={page >= numPages} icon={<ChevronRight size={12} />} />
          </Space.Compact>
        )}

        {/* Zoom */}
        <Space.Compact size="small">
          <Button onClick={handleZoomOut} disabled={scale <= 0.5} icon={<ZoomOut size={12} />} />
          <Button onClick={handleFit} style={{ minWidth: 50 }}>{Math.round(scale * 100)}%</Button>
          <Button onClick={handleZoomIn} disabled={scale >= 3} icon={<ZoomIn size={12} />} />
        </Space.Compact>

        {/* V1.0.41: View mode toggle + Print + Download + Fullscreen */}
        <Tooltip title={viewMode === 'fit-width' ? 'Chuyển sang xem cuộn tất cả trang' : 'Chuyển sang xem từng trang'}>
          <Button
            size="small"
            icon={<ScanLine size={12} />}
            onClick={() => setViewMode(m => m === 'fit-width' ? 'continuous' : 'fit-width')}
          >
            {viewMode === 'fit-width' ? 'Xem cuộn' : 'Từng trang'}
          </Button>
        </Tooltip>

        <Tooltip title="In tài liệu">
          <Button size="small" icon={<Printer size={12} />} onClick={handlePrint} disabled={!file || !!error} />
        </Tooltip>
        <Tooltip title="Tải xuống">
          <Button size="small" icon={<Download size={12} />} onClick={handleDownload} disabled={!file || !!error} />
        </Tooltip>
        <Tooltip title={isFullscreen ? 'Thoát toàn màn hình' : 'Toàn màn hình'}>
          <Button size="small" icon={<Maximize2 size={12} />} onClick={handleFullscreen} />
        </Tooltip>
      </div>

      {/* PDF area */}
      <div
        ref={containerRef}
        style={{
          flex: 1, overflow: 'auto', background: '#525659',
          padding: 16, display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 8,
          WebkitOverflowScrolling: 'touch', // smooth scroll trên iOS
        }}
      >
        {error ? (
          <div style={{ color: '#fff', padding: 40, textAlign: 'center' }}>
            <AlertCircle size={32} style={{ opacity: 0.6, marginBottom: 8 }} />
            <div>{error}</div>
          </div>
        ) : (
          <Document
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            onPassword={handlePassword}
            loading={
              <div style={{ color: '#fff', padding: 40, textAlign: 'center' }}>
                <Spin /> <div style={{ marginTop: 10, fontSize: 12 }}>Đang tải tài liệu...</div>
              </div>
            }
          >
            {viewMode === 'fit-width' && numPages && (
              <Page
                key={`p-${page}-${scale}`}
                pageNumber={page}
                width={pageWidth}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                loading={<Spin />}
              />
            )}

            {viewMode === 'continuous' && numPages && (
              <>
                {Array.from({ length: numPages }, (_, i) => (
                  <Page
                    key={`p-${i + 1}-${scale}`}
                    pageNumber={i + 1}
                    width={pageWidth}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    loading={
                      <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Spin />
                      </div>
                    }
                  />
                ))}
              </>
            )}
          </Document>
        )}

        {loading && !error && (
          <div style={{ color: '#fff', padding: 40, textAlign: 'center' }}>
            <Spin />
          </div>
        )}
      </div>

      {/* V1.0.63i: Modal nhập password cho PDF được bảo vệ */}
      <Modal
        open={passwordDialogOpen}
        title={<span><Lock size={14} style={{ verticalAlign: -2, marginRight: 8, color: '#fa8c16' }} />Tài liệu được bảo vệ</span>}
        onCancel={cancelPassword}
        onOk={submitPassword}
        okText="Mở tài liệu"
        cancelText="Hủy"
        maskClosable={false}
        width={440}
      >
        <div style={{ fontSize: 13, color: '#5b6875', marginBottom: 12 }}>
          {passwordWrong
            ? <span style={{ color: '#cf1322' }}>⚠ Mật khẩu không đúng. Vui lòng nhập lại.</span>
            : 'Tài liệu PDF này được bảo vệ bằng mật khẩu. Vui lòng nhập mật khẩu để xem.'}
        </div>
        <Input.Password
          autoFocus
          placeholder="Nhập mật khẩu PDF..."
          value={passwordInput}
          onChange={(e) => setPasswordInput(e.target.value)}
          onPressEnter={submitPassword}
        />
      </Modal>
    </div>
  );
}

export default PdfFullViewer;
